import { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { formatAddress } from "@medoma/models/addresses";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import {
  getPatientFamilyName,
  getPatientGivenName,
  personalIdentityNumberTypeSchema,
  type IRegisteredPatient,
} from "@medoma/models/patients";
import {
  localizePhoneNumber,
  standardizePhoneNumber,
} from "@medoma/models/phoneNumbers";
import { conventionalPostalCodeSchema } from "@medoma/models/postalCode";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import Form from "@/components/Form/Form";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import {
  admitPatient,
  informationFieldConfigValueSchema,
  usePatientInformationFields,
  useTeams,
} from "@/api/Patients";
import type { IAdmitPatient } from "@/api/Patients";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import AddressInput, { type AddressInputFields } from "./AddressInput";
import AdmissionDateInput, {
  type AdmissionDateInputFields,
} from "./AdmissionDateInput";
import AreaSelect, { type AreaSelectFields } from "./AreaSelect";
import CareSummaryInput, {
  type CareSummaryInputFields,
} from "./CareSummaryInput";
import CloseContactInput, {
  ExistingCloseContacts,
  type CloseContactInputFields,
} from "./CloseContactInput";
import DiagnosisCodeInput, {
  type DiagnosisCodeInputFields,
} from "./DiagnosisCodeInput";
import ExternalCareEpisodeId, {
  type ExternalCareEpisodeIdInputFields,
} from "./ExternalCareEpisodeIdInput";
import FlagsInput, { type FlagsInputFields } from "./FlagsInput";
import GeneralInformationInput, {
  type GeneralInformationInputFields,
} from "./GeneralInformationInput";
import HomeCareCheckbox, {
  type HomeCareCheckboxFields,
} from "./HomeCareCheckbox";
import PatientIdentifierInput, {
  type PatientIdentifierInputFields,
} from "./PatientIdentifierInput";
import PatientNameInput, {
  type PatientNameInputFields,
} from "./PatientNameInput";
import PatientTypeInput, {
  type PatientTypeInputFields,
} from "./PatientTypeInput";
import PetsInput, { type PetsInputFields } from "./PetsInput";
import PhoneNumberInput, {
  type PhoneNumberInputFields,
} from "./PhoneNumberInput";
import PrimaryResponsibleInput, {
  type PrimaryResponsibleInputFields,
} from "./PrimaryResponsibleInput";
import ReasonForAdmissionInput, {
  type ReasonForAdmissionInputFields,
} from "./ReasonForAdmissionInput";
import SafetyAlarmInput, {
  type SafetyAlarmInputFields,
} from "./SafetyAlarmInput";
import TeamSelect, { type TeamSelectFields } from "./TeamSelect";
import WardInput, { type WardInputFields } from "./WardInput";

type AdmitPatientFormFields = AddressInputFields &
  AdmissionDateInputFields &
  AreaSelectFields &
  CareSummaryInputFields &
  CloseContactInputFields &
  DiagnosisCodeInputFields &
  ExternalCareEpisodeIdInputFields &
  FlagsInputFields &
  GeneralInformationInputFields &
  HomeCareCheckboxFields &
  PatientIdentifierInputFields &
  PatientNameInputFields &
  PatientTypeInputFields &
  PetsInputFields &
  PhoneNumberInputFields &
  PrimaryResponsibleInputFields &
  ReasonForAdmissionInputFields &
  SafetyAlarmInputFields &
  TeamSelectFields &
  WardInputFields;

// Identity is settled at registration and admission cannot change it: the endpoint takes
// neither name nor patient type, and the identifier is fixed for the care episode. The
// fields are still registered and shown, so the form reads as a whole patient; what the
// admission sends is picked field by field on submit.
const PatientIdentity = () => (
  <>
    <PatientNameInput readonly />
    <PatientIdentifierInput readonly />
    <PatientTypeInput readonly />
  </>
);

export const AdmitPatientForm = ({
  patient,
}: {
  patient: IRegisteredPatient;
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { address, relatives } = patient;

  const methods = useForm<AdmitPatientFormFields>({
    defaultValues: {
      // Admitting today is the common case, and the backend refuses a future date.
      admissionDate: machineFormat(new Date(), "yyyy-MM-dd"),
      givenName: getPatientGivenName(patient.name),
      familyName: getPatientFamilyName(patient.name),
      patientType: patient.patientType,
      identifier: patient.personalIdentityNumber,
      isTemporaryNumber:
        patient.personalIdentityNumberType ===
        personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber,
      addressLine1: address.addressLine1,
      postalCode: address.postalCode,
      city: address.city,
      coordinates: address.coordinates,
      additionalAddressInformation: address.additionalInformation ?? undefined,
      keyToPatient: address.keyToPatient ?? undefined,
      areaId: patient.area?.id.toString(),
      teamId: patient.team?.id.toString(),
      wardId: patient.ward.id.toString(),
      phoneNumber: patient.phoneNumber
        ? localizePhoneNumber({ countryCode, phoneNumber: patient.phoneNumber })
        : undefined,
      diagnosisCode: patient.diagnosis?.code,
      externalCareEpisodeId: patient.externalCareEpisodeId ?? undefined,
      flags: patient.flags ?? undefined,
      generalInformation: patient.information ?? undefined,
      homeCare: patient.homeCare ?? undefined,
      pets: patient.pet ?? undefined,
      careSummary: patient.careSummary ?? undefined,
      primaryResponsibleId: patient.primaryResponsible?.toString(),
      reasonForAdmission: patient.reasonForAdmission ?? undefined,
      safetyAlarmText: patient.safetyAlarmText ?? undefined,
    },
  });

  const { setError, reset, getValues } = methods;

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const { data: wardEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );

  const { data: teams } = useTeams();

  const { mutate, isPending: isPendingAdmitPatient } = useMutation({
    mutationFn: (admission: IAdmitPatient) =>
      admitPatient({ patientId: patient.id, admission }),
    onSuccess: async () => {
      // Admission changes the patient's status, which is read all over the app.
      await queryClient.invalidateQueries();
      // Absolute, so the form lands in the same place wherever it is mounted.
      void navigate(`/commandcenter/patients/${patient.id}/activities/new`);
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
  });

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  const otherInformationFields = [
    {
      condition:
        conditionalInformationFields.DiagnosisCode !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <DiagnosisCodeInput
          diagnosisCodeFieldConfig={conditionalInformationFields.DiagnosisCode}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.GeneralInformation !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <GeneralInformationInput
          generalInformationFieldConfig={
            conditionalInformationFields.GeneralInformation
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.Flags !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <FlagsInput flagsFieldConfig={conditionalInformationFields.Flags} />
      ),
    },
    {
      condition:
        conditionalInformationFields.Pet !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PetsInput petsFieldConfig={conditionalInformationFields.Pet} />
      ),
    },
    {
      condition:
        conditionalInformationFields.PersWristband !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <SafetyAlarmInput
          safetyAlarmFieldConfig={conditionalInformationFields.PersWristband}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.PrimaryResponsible !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PrimaryResponsibleInput
          primaryResponsibleFieldConfig={
            conditionalInformationFields.PrimaryResponsible
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.HomeCare !==
        informationFieldConfigValueSchema.enum.hidden,
      component: <HomeCareCheckbox />,
    },
    {
      condition:
        conditionalInformationFields.ReasonForAdmission !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <ReasonForAdmissionInput
          reasonForAdmissionFieldConfig={
            conditionalInformationFields.ReasonForAdmission
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.CareSummary !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <CareSummaryInput
          careSummaryFieldConfig={conditionalInformationFields.CareSummary}
        />
      ),
    },
  ];

  const hasAnyOtherInformation = otherInformationFields.some(
    (field) => field.condition,
  );

  const validateAndSubmit = methods.handleSubmit(async (data) => {
    // The fieldset is absent when the organization has switched the field off or the patient
    // already has close contacts. A name and a relation are what make a close contact;
    // without them there is no contact to send. The phone number is optional, so an
    // untouched field travels as "not given" rather than as an empty phone number.
    const { closeContact } = data;
    const relative =
      closeContact?.name && closeContact.relation
        ? {
            name: closeContact.name,
            phone: closeContact.phoneNumber
              ? standardizePhoneNumber({
                  countryCode,
                  phoneNumber: closeContact.phoneNumber,
                })
              : null,
            isLivingTogether: closeContact.isLivingTogether,
            relation: closeContact.relation,
          }
        : null;

    const admission: IAdmitPatient = {
      admissionDate: calendarDateSchema.decode(data.admissionDate),
      address: formatAddress({
        addressLine1: data.addressLine1,
        postalCode: conventionalPostalCodeSchema.shape[countryCode].parse(
          data.postalCode,
        ),
        city: data.city,
        coordinates: data.coordinates,
        // The address input calls the field `additionalAddressInformation`; the address
        // itself calls it `additionalInformation`.
        additionalInformation: data.additionalAddressInformation,
        keyToPatient: data.keyToPatient,
        countryCode,
      }),
      area: data.areaId ? Number(data.areaId) : null,
      team: data.teamId ? Number(data.teamId) : null,
      externalCareEpisodeId: data.externalCareEpisodeId,
      phone: standardizePhoneNumber({
        countryCode,
        phoneNumber: data.phoneNumber,
      }),
      ward: data.wardId ? Number(data.wardId) : null,
      careSummary: data.careSummary,
      diagnosisCode: data.diagnosisCode,
      flags: data.flags,
      homeCare: data.homeCare,
      information: data.generalInformation,
      pet: data.pets,
      primaryResponsible: data.primaryResponsibleId
        ? Number(data.primaryResponsibleId)
        : null,
      reasonForAdmission: data.reasonForAdmission,
      relative,
      safetyAlarmText: data.safetyAlarmText,
    };

    mutate(admission);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <AdmissionDateInput registeredAt={patient.registeredAt} />
        <PatientIdentity />
        {conditionalInformationFields.ExternalCareEpisodeId !==
        informationFieldConfigValueSchema.enum.hidden ? (
          <ExternalCareEpisodeId
            externalCareEpisodeIdFieldConfig={
              conditionalInformationFields.ExternalCareEpisodeId
            }
          />
        ) : null}
        {wardEnabled ? <WardInput /> : null}
        <PhoneNumberInput isRequired />
        <AddressInput
          additionalAddressInformationFieldConfig={
            conditionalInformationFields.AdditionalAdressInformation
          }
          keyToPatientFieldConfig={conditionalInformationFields.KeyToPatient}
        />
        {areaEnabled ? <AreaSelect /> : null}
        {teams && teams.length > 0 ? <TeamSelect /> : null}
        {hasAnyOtherInformation ? (
          <Fieldset legend={t`Övrig information`} visualStyle="framed">
            <Form.Section>
              {otherInformationFields
                .filter((field) => field.condition)
                .map((field, index) => (
                  <Fragment key={index}>{field.component}</Fragment>
                ))}
            </Form.Section>
          </Fieldset>
        ) : null}
        {conditionalInformationFields.CloseContact !==
        informationFieldConfigValueSchema.enum.hidden ? (
          relatives.length > 0 ? (
            <ExistingCloseContacts relatives={relatives} />
          ) : (
            <CloseContactInput
              closeContactFieldConfig={
                conditionalInformationFields.CloseContact
              }
            />
          )
        ) : null}

        {methods.formState.errors.root?.server?.message ? (
          <ErrorMessage
            message={methods.formState.errors.root.server.message}
          />
        ) : null}

        <FilledButton type="submit" disabled={isPendingAdmitPatient}>
          {isPendingAdmitPatient ? <Spinner /> : null}
          <Trans>Fullfölj inskrivning</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};
